import React, { useState, useEffect } from 'react';
import { useMiniERP } from './hooks/useMiniERP';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faToggleOn, faToggleOff, faCalendarCheck, faLock,
  faChartPie, faChevronRight, faArrowLeft, faTrash, faSave, faTimes, faLandmark
} from '@fortawesome/free-solid-svg-icons';

const AUDIT_STATUSES = ['Not Started', 'In Progress', 'Completed'];

const inPeriod = (date, period) => {
  const d = new Date(date);
  return d >= new Date(period.startDate) && d <= new Date(period.endDate);
};

// Same balance logic as Reports.jsx's Ledger View, scoped to the chosen
// period's date range instead of all-time — this is what makes "drilling
// into a period" actually mean something rather than just being a filter.
const computeLedgerBalance = (ledger, vouchers, period) => {
  let net = 0;
  vouchers.forEach(v => {
    if (!v.isActive || !inPeriod(v.date, period)) return;
    v.entries.forEach(en => {
      const id = en.ledgerId?._id || en.ledgerId;
      if (id === ledger._id) net += (en.debit || 0) - (en.credit || 0);
    });
  });
  const opening = ledger.balanceType === 'Debit' ? (ledger.openingBalance || 0) : -(ledger.openingBalance || 0);
  const signed = opening + net;
  return ledger.balanceType === 'Debit' ? signed : -signed;
};

const emptyProfile = {
  companyName: '', cin: '', gstn: '', address: '',
  area: '', city: '', state: '', country: '', pinCode: '',
  auditorName: '', auditStatus: 'Not Started',
};

const MiniERPAccountingPeriod = ({ authState }) => {
  const {
    fetchAccountingPeriods,
    createAccountingPeriod,
    updateAccountingPeriod,
    toggleAccountingPeriod,
    fetchLedgers,
    fetchVouchers,
    updateVoucher,
  } = useMiniERP();

  const { status, showSuccess, showError } = useStatusBanner();
  const [listStatus, setListStatus] = useState(null);

  const [periodsList, setPeriodsList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);

  const [formData, setFormData] = useState({
    periodName: '', startDate: '', endDate: '', isActive: true, ...emptyProfile,
  });

  // ================= DRILL-DOWN STATE =================
  const [drillPeriod, setDrillPeriod] = useState(null); // the period object, or null = not drilling
  const [drillGroup, setDrillGroup] = useState(null);   // 'Balance Sheet' | 'Profit & Loss'
  const [drillSubgroup, setDrillSubgroup] = useState(null);
  const [drillLedger, setDrillLedger] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const [editingVoucher, setEditingVoucher] = useState(null);
  const [voucherForm, setVoucherForm] = useState(null);
  const { status: vStatus, showSuccess: vShowSuccess, showError: vShowError } = useStatusBanner();

  const loadPeriods = async () => {
    const res = await fetchAccountingPeriods();
    setPeriodsList(res?.data || []);
  };

  useEffect(() => {
    loadPeriods();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      showError('End date must be after start date.');
      return;
    }
    try {
      if (editingPeriod) {
        await updateAccountingPeriod(editingPeriod._id, formData);
        showSuccess('Accounting period updated!');
      } else {
        await createAccountingPeriod(formData);
        showSuccess('Accounting period created!');
      }
      await loadPeriods();
      setTimeout(() => { setShowModal(false); resetForm(); }, 700);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleAccountingPeriod(id);
      await loadPeriods();
      setListStatus({ type: 'success', message: 'Status updated.' });
      setTimeout(() => setListStatus(null), 2500);
    } catch (err) {
      setListStatus({ type: 'error', message: 'Toggle failed: ' + err.message });
    }
  };

  const openCreateModal = () => {
    resetForm();
    // Pre-fill address fields from the user's own profile — editable, just a sensible default
    setFormData(f => ({
      ...f,
      area: authState?.user?.area || '',
      city: authState?.user?.city || '',
      state: authState?.user?.state || '',
      country: authState?.user?.country || '',
      pinCode: authState?.user?.pinCode || '',
    }));
    setShowModal(true);
  };

  const openEditModal = (period) => {
    setEditingPeriod(period);
    setFormData({
      periodName: period.periodName,
      startDate: period.startDate.split('T')[0],
      endDate: period.endDate.split('T')[0],
      isActive: period.isActive,
      companyName: period.companyName || '',
      cin: period.cin || '',
      gstn: period.gstn || '',
      address: period.address || '',
      area: period.area || '',
      city: period.city || '',
      state: period.state || '',
      country: period.country || '',
      pinCode: period.pinCode || '',
      auditorName: period.auditorName || '',
      auditStatus: period.auditStatus || 'Not Started',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ periodName: '', startDate: '', endDate: '', isActive: true, ...emptyProfile });
    setEditingPeriod(null);
  };

  // ================= DRILL-DOWN LOGIC =================
  const enterDrillDown = async (period) => {
    setDrillPeriod(period);
    setDrillGroup(null);
    setDrillSubgroup(null);
    setDrillLedger(null);
    setDrillLoading(true);
    try {
      const [lRes, vRes] = await Promise.all([fetchLedgers(), fetchVouchers()]);
      setLedgers(lRes?.data || []);
      setVouchers(vRes?.data || []);
    } finally {
      setDrillLoading(false);
    }
  };

  const exitDrillDown = () => {
    setDrillPeriod(null);
    setDrillGroup(null);
    setDrillSubgroup(null);
    setDrillLedger(null);
    setEditingVoucher(null);
    setVoucherForm(null);
  };

  const subgroupsInGroup = (group) => {
    const set = new Set();
    ledgers.filter(l => l.group === group).forEach(l => set.add(l.subgroup || '(No Subgroup)'));
    return Array.from(set).sort();
  };

  const ledgersInSubgroup = (group, subgroup) =>
    ledgers.filter(l => l.group === group && (l.subgroup || '(No Subgroup)') === subgroup);

  const vouchersForLedger = (ledgerId) =>
    vouchers
      .filter(v => v.isActive && inPeriod(v.date, drillPeriod) && v.entries.some(en => (en.ledgerId?._id || en.ledgerId) === ledgerId))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

  // ================= VOUCHER EDIT (inline, within drill-down) =================
  const openEditVoucher = (voucher) => {
    setEditingVoucher(voucher);
    setVoucherForm({
      voucherType: voucher.voucherType,
      voucherNumber: voucher.voucherNumber,
      date: new Date(voucher.date).toISOString().split('T')[0],
      narration: voucher.narration || '',
      entries: voucher.entries.map(en => ({
        ledgerId: en.ledgerId?._id || en.ledgerId,
        debit: en.debit || 0,
        credit: en.credit || 0,
      })),
    });
  };

  const updateVoucherEntry = (idx, field, value) => {
    setVoucherForm(f => {
      const entries = [...f.entries];
      entries[idx] = { ...entries[idx], [field]: field === 'ledgerId' ? value : Number(value) || 0 };
      return { ...f, entries };
    });
  };

  const addVoucherEntryRow = () =>
    setVoucherForm(f => ({ ...f, entries: [...f.entries, { ledgerId: '', debit: 0, credit: 0 }] }));

  const removeVoucherEntryRow = (idx) =>
    setVoucherForm(f => ({ ...f, entries: f.entries.length > 1 ? f.entries.filter((_, i) => i !== idx) : f.entries }));

  const voucherTotals = () => {
    if (!voucherForm) return { debit: 0, credit: 0 };
    return voucherForm.entries.reduce(
      (acc, e) => ({ debit: acc.debit + (e.debit || 0), credit: acc.credit + (e.credit || 0) }),
      { debit: 0, credit: 0 }
    );
  };

  const saveVoucherEdit = async () => {
    const { debit, credit } = voucherTotals();
    if (Math.abs(debit - credit) > 0.01) {
      vShowError('Debit and Credit totals must be equal.');
      return;
    }
    const validEntries = voucherForm.entries.filter(e => e.ledgerId);
    if (validEntries.length === 0) {
      vShowError('Add at least one ledger entry.');
      return;
    }
    try {
      await updateVoucher(editingVoucher._id, { ...voucherForm, entries: validEntries });
      vShowSuccess('Voucher updated successfully!');
      const vRes = await fetchVouchers();
      setVouchers(vRes?.data || []);
      setTimeout(() => { setEditingVoucher(null); setVoucherForm(null); }, 700);
    } catch (err) {
      vShowError(err.message);
    }
  };

  // ================= BREADCRUMB =================
  const Breadcrumb = () => (
    <div className="flex flex-wrap items-center gap-2 text-xs lg:text-sm text-gray-400 mb-4">
      <button onClick={exitDrillDown} className="w-fit px-2 py-1 text-cyan-300 hover:underline">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" /> All Periods
      </button>
      <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
      <button
        onClick={() => { setDrillGroup(null); setDrillSubgroup(null); setDrillLedger(null); }}
        className={`w-fit px-1 py-1 ${!drillGroup ? 'text-white font-semibold' : 'hover:text-cyan-300'}`}
      >
        {drillPeriod?.periodName}
      </button>
      {drillGroup && (
        <>
          <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
          <button
            onClick={() => { setDrillSubgroup(null); setDrillLedger(null); }}
            className={`w-fit px-1 py-1 ${!drillSubgroup ? 'text-white font-semibold' : 'hover:text-cyan-300'}`}
          >
            {drillGroup}
          </button>
        </>
      )}
      {drillSubgroup && (
        <>
          <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
          <button
            onClick={() => setDrillLedger(null)}
            className={`w-fit px-1 py-1 ${!drillLedger ? 'text-white font-semibold' : 'hover:text-cyan-300'}`}
          >
            {drillSubgroup}
          </button>
        </>
      )}
      {drillLedger && (
        <>
          <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
          <span className="text-white font-semibold">{drillLedger.name}</span>
        </>
      )}
    </div>
  );

  // ================= DRILL-DOWN RENDER =================
  const renderDrillDown = () => {
    if (drillLoading) {
      return <div className="text-gray-500 text-sm">Loading chart of accounts...</div>;
    }

    // LEVEL 0 — pick Balance Sheet or Profit & Loss
    if (!drillGroup) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {['Balance Sheet', 'Profit & Loss'].map(g => {
            const count = ledgers.filter(l => l.group === g).length;
            return (
              <button
                key={g}
                onClick={() => setDrillGroup(g)}
                className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500/40 rounded-2xl p-6 text-left transition-colors"
              >
                <FontAwesomeIcon icon={faLandmark} className="text-3xl text-cyan-400 mb-3" />
                <div className="text-lg font-semibold text-white">{g}</div>
                <div className="text-sm text-gray-400">{count} ledger{count !== 1 ? 's' : ''}</div>
              </button>
            );
          })}
        </div>
      );
    }

    // LEVEL 1 — pick Subgroup
    if (!drillSubgroup) {
      const subgroups = subgroupsInGroup(drillGroup);
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {subgroups.map(sg => {
            const count = ledgersInSubgroup(drillGroup, sg).length;
            return (
              <button
                key={sg}
                onClick={() => setDrillSubgroup(sg)}
                className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500/40 rounded-xl p-4 text-left transition-colors"
              >
                <div className="font-medium text-white">{sg}</div>
                <div className="text-xs text-gray-500">{count} ledger{count !== 1 ? 's' : ''}</div>
              </button>
            );
          })}
          {subgroups.length === 0 && (
            <p className="text-gray-500 text-sm col-span-full">No ledgers in this group yet.</p>
          )}
        </div>
      );
    }

    // LEVEL 2 — pick Ledger
    if (!drillLedger) {
      const list = ledgersInSubgroup(drillGroup, drillSubgroup);
      return (
        <div className="space-y-2">
          {list.map(l => {
            const balance = computeLedgerBalance(l, vouchers, drillPeriod);
            return (
              <button
                key={l._id}
                onClick={() => setDrillLedger(l)}
                className="w-full flex justify-between items-center bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500/40 rounded-xl px-4 py-3 text-left transition-colors"
              >
                <div>
                  <div className="font-mono text-xs text-cyan-300">{l.ledgerCode}</div>
                  <div className="font-medium text-white">{l.name}</div>
                </div>
                <div className={`font-semibold text-sm ${balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  ₹{balance.toFixed(2)}
                </div>
              </button>
            );
          })}
          {list.length === 0 && <p className="text-gray-500 text-sm">No ledgers here.</p>}
        </div>
      );
    }

    // LEVEL 3 — Vouchers for this ledger within the period
    const ledgerVouchers = vouchersForLedger(drillLedger._id);
    return (
      <div className="space-y-2">
        {ledgerVouchers.map(v => {
          const entry = v.entries.find(en => (en.ledgerId?._id || en.ledgerId) === drillLedger._id);
          return (
            <div key={v._id} className="flex justify-between items-center bg-gray-800 border border-gray-700 rounded-xl px-4 py-3">
              <div>
                <div className="font-mono text-xs text-cyan-300">{v.voucherNumber}</div>
                <div className="text-sm text-white">{v.voucherType} • {new Date(v.date).toLocaleDateString()}</div>
                {v.narration && <div className="text-xs text-gray-500 truncate max-w-xs">{v.narration}</div>}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right text-sm">
                  {entry?.debit ? <div className="text-red-400">Dr ₹{entry.debit}</div> : null}
                  {entry?.credit ? <div className="text-green-400">Cr ₹{entry.credit}</div> : null}
                </div>
                <button
                  onClick={() => openEditVoucher(v)}
                  className="w-fit px-2 py-1 text-gray-400 hover:text-cyan-400"
                  title="Edit voucher"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              </div>
            </div>
          );
        })}
        {ledgerVouchers.length === 0 && (
          <p className="text-gray-500 text-sm">No vouchers posted to this ledger within this period.</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        {!drillPeriod ? (
          <>
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
              <div className="text-left">
                <h1 className="text-xl lg:text-3xl font-bold text-cyan-200 flex items-center gap-3">
                  <FontAwesomeIcon icon={faCalendarCheck} /> Accounting Period
                </h1>
                <p className="text-xs lg:text-sm text-gray-400">
                  Define your financial years, then tap a period to explore its Balance Sheet &amp; P/L
                </p>
              </div>
              <button
                onClick={openCreateModal}
                className="bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-xl flex items-center gap-2 font-medium"
              >
                <FontAwesomeIcon icon={faPlus} /> New Period
              </button>
            </div>

            {listStatus && <div className="mb-4"><StatusBanner status={listStatus} /></div>}

            {/* Period List */}
            <div className="bg-gray-800 rounded-3xl overflow-hidden">
              {/* ================= MOBILE CARDS ================= */}
              <div className="block lg:hidden space-y-3 p-3">
                {periodsList.map((period) => (
                  <div key={period._id} className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-xs">
                    <div className="flex justify-between items-start">
                      <button className="text-left flex-1" onClick={() => enterDrillDown(period)}>
                        <div className="text-sm font-semibold text-white flex items-center gap-2">
                          {period.periodName}
                          {period.isClosed && <FontAwesomeIcon icon={faLock} className="text-amber-400" title="Closed" />}
                        </div>
                        <div className="text-gray-400 mt-1">
                          {new Date(period.startDate).toLocaleDateString()} &rarr; {new Date(period.endDate).toLocaleDateString()}
                        </div>
                        {period.companyName && (
                          <div className="text-gray-500 mt-1">{period.companyName}</div>
                        )}
                      </button>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => enterDrillDown(period)} className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400" title="Explore">
                          <FontAwesomeIcon icon={faChartPie} />
                        </button>
                        <button onClick={() => handleToggle(period._id)} className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400">
                          <FontAwesomeIcon icon={period.isActive ? faToggleOn : faToggleOff} />
                        </button>
                        <button onClick={() => openEditModal(period)} className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ================= DESKTOP TABLE ================= */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-xs lg:text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-4 px-6 text-gray-400 font-medium">Period Name</th>
                      <th className="text-left py-4 px-6 text-gray-400 font-medium">Company</th>
                      <th className="text-left py-4 px-6 text-gray-400 font-medium">Start Date</th>
                      <th className="text-left py-4 px-6 text-gray-400 font-medium">End Date</th>
                      <th className="text-center py-4 px-6 text-gray-400 font-medium">Closed</th>
                      <th className="text-center py-4 px-6 text-gray-400 font-medium">Status</th>
                      <th className="text-center py-4 px-6 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {periodsList.map((period) => (
                      <tr key={period._id} className="hover:bg-gray-700/50 transition-colors">
                        <td className="py-4 px-6 font-medium">
                          <button onClick={() => enterDrillDown(period)} className="hover:text-cyan-300 hover:underline text-left">
                            {period.periodName}
                          </button>
                        </td>
                        <td className="py-4 px-6 text-gray-400">{period.companyName || '-'}</td>
                        <td className="py-4 px-6 text-gray-400">{new Date(period.startDate).toLocaleDateString()}</td>
                        <td className="py-4 px-6 text-gray-400">{new Date(period.endDate).toLocaleDateString()}</td>
                        <td className="py-4 px-6 text-center">
                          {period.isClosed && <FontAwesomeIcon icon={faLock} className="text-amber-400" title="Closed" />}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <button
                            onClick={() => handleToggle(period._id)}
                            className="text-xl lg:text-2xl w-fit px-2 py-1 text-gray-400 hover:text-cyan-400 transition-colors"
                          >
                            <FontAwesomeIcon icon={period.isActive ? faToggleOn : faToggleOff} />
                          </button>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex justify-center gap-3">
                            <button
                              onClick={() => enterDrillDown(period)}
                              className="text-lg lg:text-xl w-fit px-2 py-1 text-gray-400 hover:text-cyan-400 transition-colors"
                              title="Explore Balance Sheet & P/L"
                            >
                              <FontAwesomeIcon icon={faChartPie} />
                            </button>
                            <button
                              onClick={() => openEditModal(period)}
                              className="text-lg lg:text-xl w-fit px-2 py-1 text-gray-400 hover:text-cyan-400 transition-colors"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {periodsList.length === 0 && (
                <div className="text-center py-16 text-gray-500">
                  No accounting periods yet. Create your first financial year.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* ================= DRILL-DOWN VIEW ================= */}
            <Breadcrumb />
            <div className="bg-gray-800/40 rounded-3xl p-4 lg:p-6">
              {renderDrillDown()}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Period Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 md:p-8">
            <h2 className="text-2xl font-bold text-cyan-200 mb-6 text-left">
              {editingPeriod ? 'Edit Period' : 'New Accounting Period'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-400 mb-1">Period Name</label>
                <input
                  type="text"
                  value={formData.periodName}
                  onChange={(e) => setFormData({ ...formData, periodName: e.target.value })}
                  placeholder="e.g. FY 2025-26"
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                    required
                  />
                </div>
              </div>

              <div className="border-t border-gray-700 pt-5">
                <h3 className="text-sm font-semibold text-gray-300 mb-3">Company &amp; Compliance Details <span className="text-gray-500 font-normal">(optional)</span></h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">Company Name</label>
                    <input type="text" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">CIN</label>
                    <input type="text" value={formData.cin} onChange={(e) => setFormData({ ...formData, cin: e.target.value.toUpperCase() })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 font-mono" />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">GSTN</label>
                    <input type="text" value={formData.gstn} onChange={(e) => setFormData({ ...formData, gstn: e.target.value.toUpperCase() })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 font-mono" />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">Auditor Name</label>
                    <input type="text" value={formData.auditorName} onChange={(e) => setFormData({ ...formData, auditorName: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-400 mb-1 text-sm">Address</label>
                  <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <input type="text" placeholder="Area" value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-sm" />
                  <input type="text" placeholder="City" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-sm" />
                  <input type="text" placeholder="State" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-sm" />
                  <input type="text" placeholder="Country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-sm" />
                  <input type="text" placeholder="PIN" value={formData.pinCode} onChange={(e) => setFormData({ ...formData, pinCode: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-sm" />
                </div>

                <div>
                  <label className="block text-gray-400 mb-1 text-sm">Audit Status</label>
                  <select value={formData.auditStatus} onChange={(e) => setFormData({ ...formData, auditStatus: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1">
                    {AUDIT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <StatusBanner status={status} />

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium"
                >
                  {editingPeriod ? 'Update Period' : 'Create Period'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Voucher Modal (opened from drill-down Level 3) */}
      {editingVoucher && voucherForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-cyan-200">Edit Voucher {editingVoucher.voucherNumber}</h2>
              <button onClick={() => { setEditingVoucher(null); setVoucherForm(null); }} className="text-2xl w-fit p-1 text-gray-400 hover:text-white">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 mb-1 text-sm">Type</label>
                  <select
                    value={voucherForm.voucherType}
                    onChange={(e) => setVoucherForm({ ...voucherForm, voucherType: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                  >
                    {['Receipt', 'Payment', 'Journal', 'Contra', 'DebitNote', 'CreditNote'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 mb-1 text-sm">Voucher No</label>
                  <input
                    type="text"
                    value={voucherForm.voucherNumber}
                    onChange={(e) => setVoucherForm({ ...voucherForm, voucherNumber: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1 text-sm">Date</label>
                  <input
                    type="date"
                    value={voucherForm.date}
                    onChange={(e) => setVoucherForm({ ...voucherForm, date: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-400 text-sm">Ledger Entries — you can reassign any ledger, must stay in balance</label>
                  <button type="button" onClick={addVoucherEntryRow} className="text-cyan-400 hover:text-cyan-300 text-sm w-fit px-2 py-1">
                    <FontAwesomeIcon icon={faPlus} /> Add Row
                  </button>
                </div>
                <div className="space-y-2">
                  {voucherForm.entries.map((entry, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row gap-2 bg-gray-900 rounded-xl p-2">
                      <select
                        value={entry.ledgerId}
                        onChange={(e) => updateVoucherEntry(idx, 'ledgerId', e.target.value)}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-sm"
                      >
                        <option value="">Select Ledger</option>
                        {ledgers.map(l => (
                          <option key={l._id} value={l._id}>{l.ledgerCode} - {l.name}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={entry.debit}
                        onChange={(e) => updateVoucherEntry(idx, 'debit', e.target.value)}
                        placeholder="Debit"
                        className="w-full sm:w-28 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-sm text-right"
                      />
                      <input
                        type="number"
                        value={entry.credit}
                        onChange={(e) => updateVoucherEntry(idx, 'credit', e.target.value)}
                        placeholder="Credit"
                        className="w-full sm:w-28 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-sm text-right"
                      />
                      <button
                        type="button"
                        onClick={() => removeVoucherEntryRow(idx)}
                        className="w-fit px-2 py-1 text-rose-400 hover:text-rose-300 self-center"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-6 mt-2 text-sm font-medium">
                  <span>Debit: <span className="text-red-400">₹{voucherTotals().debit}</span></span>
                  <span>Credit: <span className="text-green-400">₹{voucherTotals().credit}</span></span>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-1 text-sm">Narration</label>
                <textarea
                  value={voucherForm.narration}
                  onChange={(e) => setVoucherForm({ ...voucherForm, narration: e.target.value })}
                  style={{ height: "100px" }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2"
                />
              </div>

              <StatusBanner status={vStatus} />

              <div className="flex gap-3 pt-2 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => { setEditingVoucher(null); setVoucherForm(null); }}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveVoucherEdit}
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faSave} /> Save Voucher
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniERPAccountingPeriod;